import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { DayValues, EditField } from './editCalc';

/** Базовый адрес редактирования отчетов (все запросы проверяются сервером по сессии и правам роли) */
export const EDIT_API = '/api/v2/reports/edit';

export interface EditPharmacy {
  id: number;
  username: string;
  pharmacyNum: string;
}

export type EditDateLimit = 'any' | 'yesterday' | 'last_month';

export interface EditAccess {
  users: EditPharmacy[];
  scope: string;
  dateLimit: EditDateLimit;
  /** Единственная допустимая дата (ограничение «только вчера») */
  allowedDate: string | null;
  /** Единственный допустимый месяц (ограничение «только прошлый месяц») */
  allowedMonth: string | null;
}

export interface DayResponse {
  success: boolean;
  pharmacy: { username: string; pharmacyNum: string };
  date: string;
  exists: boolean;
  fields: EditField[];
  computeOrder: string[];
  values: DayValues;
}

export interface MonthDay {
  date: string;
  exists: boolean;
  future: boolean;
  values: DayValues;
}

export interface MonthResponse {
  success: boolean;
  pharmacy: { username: string; pharmacyNum: string };
  month: string;
  fields: EditField[];
  computeOrder: string[];
  /** Остаток на конец последней записи до начала месяца (null - более ранних записей нет) */
  startBalance: number | null;
  days: MonthDay[];
}

export interface SaveResponse {
  success: boolean;
  message?: string;
  recalculatedDays?: number;
  savedDays?: number;
  /** Пересчитанный отчет месяца после сохранения (PUT /month) - заменяет повторный запрос данных */
  report?: MonthResponse;
}

/** Текст ошибки для пользователя: сообщение сервера, иначе запасной текст; обрыв связи - отдельно */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const e = error as { response?: { data?: { message?: string } }; isAxiosError?: boolean; message?: string };
  if (e && e.response) {
    return (e.response.data && e.response.data.message) || fallback;
  }
  if (e && e.isAxiosError) return 'Нет связи с сервером';
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Список аптек и ограничения даты для текущей роли (GET /api/v2/reports/users-for-edit).
 * Сервер повторно проверяет те же правила при каждом чтении и записи.
 */
export function useEditAccess(mode: 'daily' | 'monthly') {
  const [access, setAccess] = useState<EditAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/v2/reports/users-for-edit', { params: { mode } });
      if (!res.data || !res.data.success) {
        setError((res.data && res.data.message) || 'Не удалось загрузить список аптек');
        return;
      }
      setAccess({
        users: res.data.users || [],
        scope: res.data.scope || '',
        dateLimit: res.data.dateLimit || (mode === 'monthly' ? 'last_month' : 'yesterday'),
        allowedDate: res.data.allowedDate || null,
        allowedMonth: res.data.allowedMonth || null
      });
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось загрузить список аптек'));
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    load();
  }, [load]);

  return { access, loading, error, reload: load };
}
